import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import { useBulkSlipWtsp } from '../../context/BulkSlipWtspContext';
import Header from '../../component/header';

const BulkSlipProcessWtsp = () => {
    const location = useLocation();
    const navigate = useNavigate();
    
    // Extract data from context
    const { appId, language, userId, wtspDeviceId, userName } = useAppId();
    
    // Extract data from navigation state
    const { booth_no, voters } = location.state || {};

    // --- Global WhatsApp Bulk Slip Campaign Context ---
    const { campaign, startBulkSend, stopBulkSend } = useBulkSlipWtsp();

    // Check if the currently running/last campaign belongs to THIS booth
    const isCampaignMatching = campaign && campaign.boothNo === booth_no;

    // Derive sending state from global context instead of local state
    const isSending = isCampaignMatching ? campaign.isSending : false;
    const sentCount = isCampaignMatching ? campaign.sentCount : 0;
    const previewIndex = isCampaignMatching ? campaign.previewIndex : 0;
    
    // State for tracking progress (derived values still kept local for display)
    const [totalVoters, setTotalVoters] = useState(0);
    const [remainingCount, setRemainingCount] = useState(0);
    const [progressPercentage, setProgressPercentage] = useState(0);

    const lang = language || 'en';

    // Translation dictionary (Removed sending type keys)
    const t = {
        en: {
            title: "Bulk Slip Sending",
            boothNo: "Booth Number",
            totalVoters: "Total Voters",
            sent: "Sent",
            remaining: "Remaining",
            progress: "Progress",
            startBulkSend: "Start Bulk Send",
            sending: "Sending...",
            stopSend: "Stop Sending",
            slipPreview: "Live Slip Preview",
            previewing: "Previewing",
            completed: "Completed",
            live: "LIVE",
            overview: "Overview & Progress",
            stopped: "Stopped"
        },
        hi: {
            title: "बल्क स्लिप भेजना",
            boothNo: "बूथ नंबर",
            totalVoters: "कुल मतदाता",
            sent: "भेजा गया",
            remaining: "शेष",
            progress: "प्रगति",
            startBulkSend: "बल्क भेजना शुरू करें",
            sending: "भेजा जा रहा है...",
            stopSend: "भेजना रोकें",
            slipPreview: "लाइव स्लिप पूर्वावलोकन",
            previewing: "पूर्वावलोकन",
            completed: "पूर्ण",
            live: "लाइव",
            overview: "अवलोकन और प्रगति",
            stopped: "रुका हुआ"
        },
        gu: {
            title: "બલ્ક સ્લિપ મોકલવું",
            boothNo: "બૂથ નંબર",
            totalVoters: "કુલ મતદારો",
            sent: "મોકલેલ",
            remaining: "બાકી",
            progress: "પ્રગતિ",
            startBulkSend: "બલ્ક મોકલવાનું શરૂ કરો",
            sending: "મોકલાઈ રહ્યું છે...",
            stopSend: "મોકલવાનું બંધ કરો",
            slipPreview: "લાઇવ સ્લિપ પૂર્વાવલોકન",
            previewing: "પ્રિવ્યૂ",
            completed: "પૂર્ણ",
            live: "લાઇવ",
            overview: "ઓવરવ્યુ અને પ્રગતિ",
            stopped: "અટકાવેલ"
        }
    };

    const txt = t[lang] || t.en;

    // Initialize counts
    useEffect(() => {
        if (!voters || voters.length === 0) {
            alert("No data received!");
            navigate(-1);
            return;
        }

        const total = voters.length;
        setTotalVoters(total);
        setRemainingCount(total - (isCampaignMatching ? campaign.sentCount : 0));
        setProgressPercentage(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [voters, navigate]);

    // Calculate progress percentage
    useEffect(() => {
        if (totalVoters > 0) {
            const percentage = (sentCount / totalVoters) * 100;
            setProgressPercentage(percentage);
            setRemainingCount(totalVoters - sentCount);
        }
    }, [sentCount, totalVoters]);

    // --- Handle Start Bulk Send (delegates to global context) ---
    const handleStartBulkSend = async () => {
        if (!voters || voters.length === 0) {
            alert("No voters found to send to.");
            return;
        }

        await startBulkSend({
            boothNo: booth_no,
            voters,
            userId,
            appId,
            wtspDeviceId,
            userName
        });
    };

    // --- Handle Stop Bulk Send (delegates to global context) ---
    const handleStopBulkSend = async () => {
        await stopBulkSend(wtspDeviceId);
    };

    // Current voter being shown in the rotating preview
    const currentPreviewVoter = voters && voters.length > 0 ? voters[previewIndex] : null;
    const isCompleted = totalVoters > 0 && sentCount === totalVoters;
    const isStoppedMidway = !isSending && sentCount > 0 && sentCount < totalVoters;

    return (
        <div className="app-container">
            <Header title={txt.title} />

            <div className="dashboard-wrapper">

                {/* Top Info Bar */}
                <div className="top-info-bar slide-up-element" style={{ animationDelay: '0.05s' }}>
                    <div className="info-chip">
                        <div className="chip-icon booth-icon">
                            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M5 21V7l8-4v18M13 21V11l6 3v7M9 9v.01M9 12v.01M9 15v.01" />
                            </svg>
                        </div>
                        <div>
                            <div className="chip-label">{txt.boothNo}</div>
                            <div className="chip-value">{booth_no || (voters.length > 0 ? voters[0].part_no : "N/A")}</div>
                        </div>
                    </div>

                    {isCompleted && (
                        <div className="info-chip completed-chip">
                            <div className="chip-icon completed-icon">
                                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <div>
                                <div className="chip-value" style={{ color: '#065f46' }}>{txt.completed}</div>
                            </div>
                        </div>
                    )}

                    {isStoppedMidway && (
                        <div className="info-chip stopped-chip">
                            <div className="chip-icon stopped-icon">
                                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>
                            <div>
                                <div className="chip-value" style={{ color: '#991b1b' }}>{txt.stopped}</div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Main Grid Layout */}
                <div className="main-grid">

                    {/* Left Column */}
                    <div className="left-column">

                        {/* Section Label */}
                        <div className="section-header slide-up-element" style={{ animationDelay: '0.1s' }}>
                            <span className="section-header-bar"></span>
                            <h2 className="section-header-title">{txt.overview}</h2>
                        </div>

                        {/* Statistics Cards */}
                        <div className="stats-container slide-up-element" style={{ animationDelay: '0.15s' }}>
                            <div className="stat-card stat-total">
                                <div className="stat-icon">
                                    <svg width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 100-8 4 4 0 000 8zm6 3a4 4 0 100-8" />
                                    </svg>
                                </div>
                                <div className="stat-content">
                                    <div className="stat-number">{totalVoters}</div>
                                    <div className="stat-label">{txt.totalVoters}</div>
                                </div>
                            </div>

                            <div className="stat-card stat-sent">
                                <div className="stat-icon">
                                    <svg width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <div className="stat-content">
                                    <div className="stat-number">{sentCount}</div>
                                    <div className="stat-label">{txt.sent}</div>
                                </div>
                            </div>

                            <div className="stat-card stat-remaining">
                                <div className="stat-icon">
                                    <svg width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div className="stat-content">
                                    <div className="stat-number">{remainingCount}</div>
                                    <div className="stat-label">{txt.remaining}</div>
                                </div>
                            </div>
                        </div>

                        {/* Progress Bar Section */}
                        <div className="progress-section slide-up-element" style={{ animationDelay: '0.2s' }}>
                            <div className="progress-header">
                                <span className="progress-title">{txt.progress}</span>
                                <span className="progress-percentage">{progressPercentage.toFixed(1)}%</span>
                            </div>
                            <div className="progress-bar-container">
                                <div className="progress-bar-bg">
                                    <div
                                        className="progress-bar-fill"
                                        style={{ width: `${progressPercentage}%` }}
                                    >
                                        <div className="progress-bar-shine"></div>
                                    </div>
                                </div>
                            </div>
                            <div className="progress-footer">
                                <span>{sentCount} / {totalVoters} {txt.sent}</span>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="button-section slide-up-element" style={{ animationDelay: '0.25s' }}>
                            <button
                                className="start-bulk-send-btn"
                                onClick={handleStartBulkSend}
                                disabled={isSending || sentCount === totalVoters}
                                style={{ opacity: (isSending || sentCount === totalVoters) ? 0.6 : 1, cursor: (isSending || sentCount === totalVoters) ? 'not-allowed' : 'pointer' }}
                            >
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                </svg>
                                <span>{isSending ? txt.sending : txt.startBulkSend}</span>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                            </button>

                            <button
                                className="stop-bulk-send-btn"
                                onClick={handleStopBulkSend}
                                disabled={!isSending}
                                style={{ opacity: !isSending ? 0.5 : 1, cursor: !isSending ? 'not-allowed' : 'pointer' }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <rect x="6" y="6" width="12" height="12" rx="2" />
                                </svg>
                                <span>{txt.stopSend}</span>
                            </button>
                        </div>
                    </div>

                    {/* Right Column — Sticky Live Preview */}
                    <div className="right-column">
                        <div className="slip-preview-section slide-up-element" style={{ animationDelay: '0.3s' }}>
                            <div className="section-title-row">
                                <div className="section-title-group">
                                    {isSending && <span className="live-dot"></span>}
                                    <h3 className="section-title">{txt.slipPreview}</h3>
                                </div>
                                {voters && voters.length > 0 && (
                                    <span className="preview-counter">
                                        {txt.previewing} {previewIndex + 1} / {voters.length}
                                    </span>
                                )}
                            </div>

                            {currentPreviewVoter ? (
                                <SlipPreviewCard voter={currentPreviewVoter} boothNo={booth_no} />
                            ) : (
                                <div className="slip-empty-state">No voter data available for preview.</div>
                            )}
                        </div>
                    </div>

                </div>
            </div>

            <style jsx>{`
                * {
                    box-sizing: border-box;
                }

                
.header-container-white {
    background-color: #ffffff;
    padding: 15px 32px;
    border-bottom: 1px solid #e2e8f0;
    margin: 0px 0px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
    position: sticky;
    top: 0;
    z-index: 10;
}

                .app-container {
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                    background: radial-gradient(circle at 10% 0%, #eef2ff 0%, #f4f7fb 32%, #eef2f7 100%);
                    min-height: 100vh;
                }

                .dashboard-wrapper {
                    max-width: 1300px;
                    margin: 0 auto;
                    padding: 22px 26px 64px;
                }

                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(22px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .slide-up-element {
                    opacity: 0;
                    animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }

                /* Section Header */
                .section-header {
                    display: flex;
                    align-items: center;
                    gap: 11px;
                    margin-bottom: 18px;
                    margin-top: 6px;
                }

                .section-header-bar {
                    width: 5px;
                    height: 22px;
                    border-radius: 4px;
                    background: linear-gradient(180deg, #6366f1, #4338ca);
                    box-shadow: 0 0 10px rgba(99, 102, 241, 0.4);
                }

                .section-header-title {
                    font-size: 15px;
                    font-weight: 800;
                    color: #0f172a;
                    text-transform: uppercase;
                    letter-spacing: 0.7px;
                    margin: 0;
                }

                /* Top Info Bar */
                .top-info-bar {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 14px;
                    background: linear-gradient(135deg, #ffffff 0%, #fbfcff 100%);
                    border-radius: 20px;
                    padding: 20px 26px;
                    margin-bottom: 28px;
                    box-shadow: 0 6px 24px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04);
                    border: 1px solid #edf0f7;
                    position: relative;
                    overflow: hidden;
                }

                .top-info-bar::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 3px;
                    background: linear-gradient(90deg, #6366f1, #10b981, #f59e0b);
                }

                .info-chip {
                    display: flex;
                    align-items: center;
                    gap: 13px;
                    padding: 4px 20px;
                    flex: 1;
                    min-width: 200px;
                }

                .info-chip:not(:last-child) {
                    border-right: 1px solid #eef1f6;
                }

                @media (max-width: 768px) {
                    .info-chip:not(:last-child) {
                        border-right: none;
                        border-bottom: 1px dashed #eef1f6;
                        padding-bottom: 14px;
                    }
                }

                .chip-icon {
                    width: 44px;
                    height: 44px;
                    border-radius: 13px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    box-shadow: 0 4px 10px rgba(15, 23, 42, 0.08);
                }

                .booth-icon {
                    background: linear-gradient(135deg, #e0e7ff, #c7d2fe);
                    color: #4338ca;
                }

                .completed-icon {
                    background: linear-gradient(135deg, #d1fae5, #a7f3d0);
                    color: #065f46;
                }

                .stopped-icon {
                    background: linear-gradient(135deg, #fee2e2, #fecaca);
                    color: #991b1b;
                }

                .chip-label {
                    font-size: 11.5px;
                    font-weight: 700;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.6px;
                    margin-bottom: 4px;
                }

                .chip-value {
                    font-size: 19px;
                    font-weight: 800;
                    color: #0f172a;
                    letter-spacing: 0.2px;
                }

                .completed-chip, .stopped-chip {
                    flex: 0 0 auto;
                    min-width: auto;
                }

                /* Main Grid */
                .main-grid {
                    display: grid;
                    grid-template-columns: 1.55fr 1fr;
                    gap: 30px;
                    align-items: start;
                }

                @media (max-width: 992px) {
                    .main-grid {
                        grid-template-columns: 1fr;
                    }
                }

                .left-column {
                    display: flex;
                    flex-direction: column;
                }

                .right-column {
                    position: sticky;
                    top: 20px;
                }

                @media (max-width: 992px) {
                    .right-column {
                        position: static;
                    }
                }

                /* Statistics Cards */
                .stats-container {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 18px;
                    margin-bottom: 24px;
                }

                @media (max-width: 640px) {
                    .stats-container {
                        grid-template-columns: 1fr;
                    }
                }

                .stat-card {
                    background: #ffffff;
                    border-radius: 20px;
                    padding: 24px 22px;
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.05);
                    border: 1px solid #f1f5f9;
                    transition: transform 0.28s cubic-bezier(0.16,1,0.3,1), box-shadow 0.28s ease;
                    position: relative;
                    overflow: hidden;
                }

                .stat-card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 4px;
                    height: 100%;
                }

                .stat-total::before { background: linear-gradient(180deg, #3b82f6, #1e40af); }
                .stat-sent::before { background: linear-gradient(180deg, #10b981, #065f46); }
                .stat-remaining::before { background: linear-gradient(180deg, #f59e0b, #9a3412); }

                .stat-card:hover {
                    transform: translateY(-6px);
                    box-shadow: 0 18px 34px rgba(15, 23, 42, 0.12);
                }

                .stat-icon {
                    width: 54px;
                    height: 54px;
                    border-radius: 15px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    box-shadow: 0 4px 10px rgba(15, 23, 42, 0.06);
                }

                .stat-total .stat-icon {
                    background: linear-gradient(135deg, #dbeafe, #bfdbfe);
                    color: #1e40af;
                }

                .stat-sent .stat-icon {
                    background: linear-gradient(135deg, #d1fae5, #a7f3d0);
                    color: #065f46;
                }

                .stat-remaining .stat-icon {
                    background: linear-gradient(135deg, #fed7aa, #fdba74);
                    color: #9a3412;
                }

                .stat-content {
                    flex: 1;
                    min-width: 0;
                }

                .stat-number {
                    font-size: 32px;
                    font-weight: 800;
                    line-height: 1;
                    margin-bottom: 7px;
                    font-variant-numeric: tabular-nums;
                    letter-spacing: -0.5px;
                }

                .stat-total .stat-number { color: #1e40af; }
                .stat-sent .stat-number { color: #065f46; }
                .stat-remaining .stat-number { color: #9a3412; }

                .stat-label {
                    font-size: 12.5px;
                    font-weight: 700;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 0.6px;
                }

                /* Progress Bar Section */
                .progress-section {
                    background: #ffffff;
                    border-radius: 20px;
                    padding: 24px 26px;
                    margin-bottom: 24px;
                    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.05);
                    border: 1px solid #f1f5f9;
                }

                .progress-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 14px;
                }

                .progress-title {
                    font-size: 13.5px;
                    font-weight: 700;
                    color: #0f172a;
                    text-transform: uppercase;
                    letter-spacing: 0.6px;
                }

                .progress-percentage {
                    font-size: 22px;
                    font-weight: 800;
                    background: linear-gradient(135deg, #10b981, #059669);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    letter-spacing: -0.4px;
                }

                .progress-bar-container {
                    margin-bottom: 12px;
                }

                .progress-bar-bg {
                    width: 100%;
                    height: 13px;
                    background: #eef2f6;
                    border-radius: 999px;
                    overflow: hidden;
                    position: relative;
                    box-shadow: inset 0 1px 4px rgba(15, 23, 42, 0.12);
                }

                .progress-bar-fill {
                    height: 100%;
                    background: linear-gradient(90deg, #10b981, #059669);
                    border-radius: 999px;
                    transition: width 0.6s cubic-bezier(0.16, 1, 0.3, 1);
                    position: relative;
                    overflow: hidden;
                    box-shadow: 0 0 12px rgba(16, 185, 129, 0.45);
                }

                .progress-bar-shine {
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.55), transparent);
                    animation: shine 2s infinite;
                }

                @keyframes shine {
                    0% { left: -100%; }
                    100% { left: 100%; }
                }

                .progress-footer {
                    text-align: center;
                    font-size: 12.5px;
                    font-weight: 600;
                    color: #94a3b8;
                }

                /* Button Section */
                .button-section {
                    display: flex;
                    gap: 14px;
                    padding: 10px 0;
                }

                @media (max-width: 560px) {
                    .button-section {
                        flex-direction: column;
                    }
                }

                .start-bulk-send-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 12px;
                    flex: 1.6;
                    padding: 19px 32px;
                    background: linear-gradient(135deg, #10b981, #059669);
                    color: white;
                    border: none;
                    border-radius: 16px;
                    font-size: 16.5px;
                    font-weight: 700;
                    cursor: pointer;
                    box-shadow: 0 12px 24px rgba(16, 185, 129, 0.3);
                    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                    letter-spacing: 0.3px;
                }

                .start-bulk-send-btn:hover:not(:disabled) {
                    transform: translateY(-3px);
                    box-shadow: 0 16px 32px rgba(16, 185, 129, 0.42);
                    background: linear-gradient(135deg, #059669, #047857);
                }

                .start-bulk-send-btn:active:not(:disabled) {   
                    transform: translateY(-1px);
                }

                .stop-bulk-send-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    flex: 1;
                    padding: 19px 26px;
                    background: linear-gradient(135deg, #ffffff, #fef2f2);
                    color: #dc2626;
                    border: 2px solid #fecaca;
                    border-radius: 16px;
                    font-size: 15.5px;
                    font-weight: 700;
                    cursor: pointer;
                    box-shadow: 0 6px 16px rgba(220, 38, 38, 0.08);
                    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                    letter-spacing: 0.3px;
                }

                .stop-bulk-send-btn:hover:not(:disabled) {
                    transform: translateY(-3px);
                    background: linear-gradient(135deg, #dc2626, #b91c1c);
                    color: #ffffff;
                    border-color: #dc2626;
                    box-shadow: 0 14px 28px rgba(220, 38, 38, 0.32);
                }

                .stop-bulk-send-btn:active:not(:disabled) {
                    transform: translateY(-1px);
                }

                /* Slip Preview Section */
                .slip-preview-section {
                    background: #ffffff;
                    border-radius: 22px;
                    padding: 22px;
                    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.07);
                    border: 1px solid #f1f5f9;
                    position: relative;
                }

                .slip-preview-section::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 22px;
                    right: 22px;
                    height: 3px;
                    background: linear-gradient(90deg, #6366f1, #10b981);
                    border-radius: 0 0 4px 4px;
                }

                .section-title-row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 18px;
                    flex-wrap: wrap;
                    gap: 8px;
                }

                .section-title-group {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                }

                .live-dot {
                    width: 10px;
                    height: 10px;
                    border-radius: 50%;
                    background: #ef4444;
                    box-shadow: 0 0 0 rgba(239, 68, 68, 0.5);
                    animation: pulseDot 1.4s infinite;
                }

                @keyframes pulseDot {
                    0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.5); }
                    70% { box-shadow: 0 0 0 9px rgba(239, 68, 68, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
                }

                .section-title {
                    font-size: 17.5px;
                    font-weight: 800;
                    color: #0f172a;
                    margin: 0;
                    letter-spacing: -0.2px;
                }

                .preview-counter {
                    font-size: 12px;
                    font-weight: 700;
                    color: #059669;
                    background: linear-gradient(135deg, #d1fae5, #a7f3d0);
                    padding: 6px 14px;
                    border-radius: 20px;
                    letter-spacing: 0.3px;
                }

                .slip-empty-state {
                    background: #f8fafc;
                    border-radius: 16px;
                    padding: 44px;
                    text-align: center;
                    color: #94a3b8;
                    font-weight: 600;
                    border: 2px dashed #e2e8f0;
                }
            `}</style>
        </div>
    );
};

// --- BJP-style Live Rotating Slip Preview Card (with Top Image Banner) ---
const SlipPreviewCard = ({ voter, boothNo }) => {
    if (!voter) return null;

    const fullName = [voter.eng_f_name, voter.eng_m_name, voter.eng_surname]
        .filter(Boolean)
        .join(' ') || 'N/A';

    return (
        <div style={{
            border: '1px solid #e2e8f0',
            borderRadius: '20px',
            overflow: 'hidden',
            background: '#ffffff',
            boxShadow: '0 18px 42px rgba(15, 23, 42, 0.12)',
            maxWidth: '480px',
            margin: '0 auto',
            fontFamily: "'Noto Sans Gujarati', 'Segoe UI', sans-serif",
            transition: 'all 0.4s cubic-bezier(0.16,1,0.3,1)'
        }}>

            {/* Top Image Banner */}
            <div style={{ width: '100%', borderBottom: '3px solid #16a34a', background: '#f1f5f9', position: 'relative' }}>
                <img
                    src="https://rajkotcorporation.bjpwings.com/img/prachar/19042026030352_1.jpg"
                    alt="Banner"
                    style={{
                        width: '100%',
                        height: '100%',
                        display: 'block',
                        objectFit: 'cover'
                    }}
                />
            </div>

            {/* Voter Details */}
            <div style={{ padding: '22px 24px 24px', fontSize: '14.5px', color: '#1e293b', lineHeight: 1.9 }}>
                <div style={{
                    display: 'inline-block',
                    background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
                    color: '#065f46',
                    fontWeight: 700,
                    fontSize: '13px',
                    padding: '7px 16px',
                    borderRadius: '10px',
                    marginBottom: '14px',
                    boxShadow: '0 3px 10px rgba(6, 95, 70, 0.1)'
                }}>
                    ભાગ નં - {boothNo || voter.part_no || '-'} &nbsp;|&nbsp; બૂથ નં - {voter.part_no || '-'} &nbsp;|&nbsp; ક્રમ નં. - {voter.slnoinpart || '-'}
                </div>
                <p style={{ margin: '0 0 9px 0' }}><b>નામ :</b> {fullName}</p>
                <p style={{ margin: '0 0 9px 0' }}><b>આઈડિકાર્ડ :</b> {voter.idcard_no || 'N/A'}</p>
                <p style={{ margin: 0 }}><b>મતદાન સ્થળ :</b> {voter.eng_polling_location || 'N/A'}</p>
            </div>
        </div>

    );
};

export default BulkSlipProcessWtsp;